/**
 * Video opening cinematic (AI-generated keyframes → rendered MP4, see art/ux/cine). Plays after the
 * New Evolution tap (so the audio context is unlocked), `playsinline` + muted (the file has no audio
 * track — music/SFX/voice are driven live from introCues.ts in sync with currentTime).
 * Resolves 'done' when finished/skipped, or 'failed' if the video can't play (caller falls back to
 * the in-engine Intro).
 */
import type { AudioApi } from '@core/services';
import { el, escapeHtml } from '../dom';
import { ICONS } from '../icons';
import { VIDEO_DURATION, VIDEO_LINES, VIDEO_SFX } from './introCues';

const BASE = import.meta.env.BASE_URL ?? '/';
export const INTRO_VIDEO_URL = `${BASE}ui/cine/intro.mp4`;
export const INTRO_POSTER_URL = `${BASE}ui/cine/intro-poster.jpg`;

export class IntroVideo {
  readonly root = el('div', 'screen cine video-cine');
  private video: HTMLVideoElement;
  private finish?: (r: 'done' | 'failed') => void;

  constructor(private readonly audio: AudioApi) {
    this.video = this.makeVideo();
  }

  private makeVideo(): HTMLVideoElement {
    const v = el('video', 'cine-video');
    v.muted = true;
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    v.preload = 'auto';
    v.poster = INTRO_POSTER_URL;
    v.src = INTRO_VIDEO_URL;
    return v;
  }

  /** Start fetching early (called when the title shows) so playback starts instantly. */
  preload(): void {
    if (this.video.readyState === 0) this.video.load();
  }

  get playing(): boolean {
    return !!this.finish;
  }

  play(opts: { subtitles: boolean }): Promise<'done' | 'failed'> {
    const v = this.video;
    this.root.innerHTML = '';
    const subs = el('div', 'subs', '<span class="who">The Scientist</span><span class="t"></span>');
    const subT = subs.querySelector('.t') as HTMLElement;
    const hint = el('div', 'skip-hint show', `${ICONS.skip}<span>Tap to skip</span>`);
    this.root.append(v, subs, hint);
    this.root.classList.add('visible');
    const firedSfx = new Set<number>();
    const firedLines = new Set<number>();
    let raf = 0;
    let started = false;

    return new Promise((resolve) => {
      const end = (r: 'done' | 'failed') => {
        if (!this.finish) return;
        this.finish = undefined;
        cancelAnimationFrame(raf);
        clearTimeout(stallTimer);
        v.pause();
        v.removeEventListener('error', onError);
        this.audio.duck(0, 0.1);
        this.root.classList.remove('visible');
        resolve(r);
      };
      this.finish = end;
      const onError = () => end(started ? 'done' : 'failed');
      v.addEventListener('error', onError);
      // If playback hasn't begun within 4 s (slow network / blocked), fall back.
      const stallTimer = setTimeout(() => {
        if (!started) end('failed');
      }, 4000);

      const tick = () => {
        raf = requestAnimationFrame(tick);
        const t = v.currentTime;
        if (!started && t > 0) {
          started = true;
          clearTimeout(stallTimer);
          this.audio.music('intro', { fade: 0.3 });
          this.audio.duck(0.15, VIDEO_DURATION);
        }
        if (!started) return;
        VIDEO_SFX.forEach((c, i) => {
          if (!firedSfx.has(i) && t >= c.at) {
            firedSfx.add(i);
            // Skip cues we've already passed by more than a moment (e.g. after a stall).
            if (t - c.at < 0.35) this.audio.sfx(c.name, { volume: c.volume, pitch: c.pitch, variance: 0 });
          }
        });
        let line = '';
        VIDEO_LINES.forEach((l, i) => {
          if (t >= l.at && t < l.end) {
            line = l.text;
            if (!firedLines.has(i)) {
              firedLines.add(i);
              this.audio.voice(l.text, { mood: l.mood });
            }
          }
        });
        const html = escapeHtml(line).replace(/\*burp\*/gi, '<em>*burp*</em>');
        if (subT.innerHTML !== html) subT.innerHTML = html;
        subs.classList.toggle('show', opts.subtitles && !!line);
        if (v.ended || t >= VIDEO_DURATION - 0.04) end('done');
      };
      v.currentTime = 0;
      const p = v.play();
      if (p) p.catch(() => end('failed'));
      raf = requestAnimationFrame(tick);
    });
  }

  skip(): void {
    this.finish?.('done');
  }
}
